export const DATA_VIZ_LEVELS = [
    { id: 1, title: 'Geographic Distribution', description: 'Analyze customer locations and transaction value by region.', color: '#10b981' },
    { id: 2, title: 'Financial Profile Analysis', description: 'Understand income and transaction distributions.', color: '#3b82f6' },
    { id: 3, title: 'Behavioral Drivers', description: 'Discover relationships between financial profiles and spending behaviors.', color: '#8b5cf6' },
    { id: 4, title: 'Synthesis', description: 'Combine geography and value to find the highest value customer segments.', color: '#f97316' }
];

export const DATA_VIZ_CHALLENGES = [
    {
        id: 'customer-locations-map',
        level: 1,
        title: 'Q1: Where are our customers located?',
        businessContext: `Before we can dive into revenue or behavior, we need to answer a foundational question: **where does our customer base actually live?** Understanding geographic distribution tells us which markets we're strong in - and which we're missing entirely.`,
        teach: `We'll count the number of customers in each country using the \`customers\` table. Group by \`country\` and count \`customer_id\` to build a global picture. Use the **Map** chart type to see it geographically.`,
        explanation: `The map reveals where our customer base is concentrated. Countries with large volumes give us confidence in those markets - and the gaps show untapped regions where targeted acquisition campaigns could unlock new growth.`,
        expectedChartType: 'map',
        goal: 'View the total number of customers per country.',
        answer: {
            table: 'customers',
            groupBy: [{ column: 'country' }],
            aggregation: { fn: 'COUNT', column: 'customer_id' }
        },
        hint: `**Step-by-step**:
        1. Select the **customers** table.
        2. Set the Metric to **COUNT** of **customer_id**.
        3. Add a Group By for **country**.
        4. Run the query and select the **Map** chart type.`
    },
    {
        id: 'revenue-by-region',
        level: 1,
        title: 'Q2: Which regions generate the most total transaction value?',
        businessContext: `Knowing *where* customers are is just the start - the real question is **where is the money coming from?** A country with fewer customers might still dominate revenue if those customers spend more. Let's map total transaction value by country.`,
        teach: `Revenue data lives in the \`transactions\` table (the \`amount\` column), but geography lives in \`customers\`. You'll need to **JOIN** these two tables on \`customer_id\`, then group by \`country\` and sum the \`amount\`.`,
        explanation: `Revenue concentration often surprises - a country with fewer customers can dominate income if those customers spend more. Comparing this map with Q1 reveals which markets are efficient (high revenue per customer) versus which need volume investment.`,
        expectedChartType: 'map',
        goal: 'Join tables to map total transaction amount by country.',
        answer: {
            table: 'transactions',
            join: { table: 'customers', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'country' }],
            aggregation: { fn: 'SUM', column: 'amount' }
        },
        hint: `**Step-by-step**:
        1. Select the **transactions** table.
        2. Join the **customers** table on \`customer_id\`.
        3. Set the Metric to **SUM** of **amount**.
        4. Add a Group By for **country**.
        5. Run the query and view as a Map.`
    },
    {
        id: 'volume-vs-quality-over-time',
        level: 1,
        title: 'Q3: Volume vs Quality Over Time',
        businessContext: `Total revenue growing is good - but **is it growing because of more transactions, or bigger ones?** These tell very different business stories. More transactions = user engagement growing. Bigger transactions = customers spending more per visit. Let's find out which is driving our growth.`,
        teach: `This requires a **dual-axis chart**: one axis for transaction \`count\` (volume) and another for average \`amount\` (quality). Group by \`transaction_date\` at **Month** granularity to see monthly trends for both metrics side by side.`,
        explanation: `A growing transaction count alongside a stable or rising average amount signals healthy compounding growth. If volume climbs but average amount falls, it may indicate a shift toward lower-value use cases or the effect of incentive-driven acquisition.`,
        expectedChartType: 'dual-axis',
        goal: 'Create a dual-axis chart tracking transaction volume (bar) and average amount (line) monthly.',
        answer: {
            table: 'transactions',
            metrics: [
                { fn: 'COUNT', column: 'transaction_id' },
                { fn: 'AVG', column: 'amount' }
            ],
            groupBy: [{ column: 'transaction_date', granularity: 'Month' }]
        },
        hint: `**Step-by-step**:
        1. Select the **transactions** table.
        2. Set two Metrics: **COUNT** of \`transaction_id\` and **AVG** of \`amount\`.
        3. Add a Group By for **transaction_date**, setting granularity to **Month**.
        4. Run query and select the **Dual Axis** chart type.`
    },
    {
        id: 'income-distribution',
        level: 2,
        title: 'Q4: What does the distribution of customer income look like?',
        businessContext: `Now let's profile our customers financially. **Income is a continuous number** - individual values aren't useful for a chart. Instead, we need to group customers into income brackets (e.g. $0-$20k, $20k-$40k) to see how the population is distributed. This is where the **Bucket** feature comes in.`,
        teach: `Select the \`customers\` table, group by \`income\`, and set a **bucket size** (try 20000) to automatically create income ranges. Count \`customer_id\` within each bucket. Use a **Bar** or **Histogram** chart to visualize the distribution.`,
        explanation: `The shape of the income distribution tells us who our product appeals to. A right-skewed curve (many lower-income customers, few high earners) suggests a mass-market product. A flatter or left-skewed curve points to a more premium user base - which has implications for pricing and messaging.`,
        expectedChartType: 'histogram',
        goal: 'Group customers by income bucket to see the distribution.',
        answer: {
            table: 'customers',
            groupBy: [{ column: 'income', bucket: { size: 20000 } }],
            aggregation: { fn: 'COUNT', column: 'customer_id' }
        },
        hint: `**Step-by-step**:
        1. Select the **customers** table.
        2. Set Metric to **COUNT** of \`customer_id\`.
        3. Add a Group By for **income**, then set the **bucket size** to \`20000\`.
        4. Select the **Histogram** or **Bar** chart type.`
    },
    {
        id: 'transaction-amount-distribution',
        level: 2,
        title: 'Q5: What does the distribution of transaction amounts look like?',
        businessContext: `Are most of our transactions small day-to-day purchases, or are customers regularly making large-value transfers? **The shape of the distribution tells us a lot about user intent.** A uniform spread suggests varied use cases; a right-skewed distribution means most transactions are small with rare high-value outliers.`,
        teach: `From the \`transactions\` table, group by \`amount\` with a **bucket size** (e.g. 100) to create ranges like $0-$100, $100-$200, etc. Count \`transaction_id\` within each bucket and view as a **Histogram** to see the spread.`,
        explanation: `A right-skewed transaction distribution - most transactions small, a long tail of high-value ones - is typical for financial platforms. Knowing where the bulk of transactions cluster helps calibrate fraud detection thresholds, fee structures, and payment infrastructure sizing.`,
        expectedChartType: 'histogram',
        goal: 'Group transactions by amount bucket to view the spread.',
        answer: {
            table: 'transactions',
            groupBy: [{ column: 'amount', bucket: { size: 100 } }],
            aggregation: { fn: 'COUNT', column: 'transaction_id' }
        },
        hint: `**Step-by-step**:
        1. Select the **transactions** table.
        2. Add a Group By for **amount** and set a **bucket size** (try \`100\`).
        3. Set Metric to **COUNT** of \`transaction_id\`.
        4. Run and view as a **Histogram** or **Bar** chart.`
    },
    {
        id: 'income-vs-spending-scatter',
        level: 3,
        title: 'Q6: Do higher income customers spend more overall?',
        businessContext: `We'd expect that higher-income customers spend more - but **is the relationship actually linear?** Maybe income plateaus at a certain point, or lower-income customers make up for it with frequency. A scatter plot lets us see the true shape of this relationship without averaging it away.`,
        teach: `Join \`customers\` and \`transactions\` on \`customer_id\`. Group by \`customer_id\` so each dot on the scatter represents one customer. Add **AVG(income)** as the X-axis metric and **SUM(amount)** as the Y-axis. Select the **Scatter** chart type. Filter to recent customers (\`signup_date >= 2024-01-01\`) to keep the chart readable.`,
        explanation: `The scatter reveals whether the income-spending relationship is linear, plateaus at a certain income level, or shows surprising outliers. Customers who spend well above their income level are often high-engagement power users worth prioritising for retention.`,
        expectedChartType: 'scatter',
        goal: 'Create a scatter plot of total transaction amount vs income per customer.',
        answer: {
            table: 'customers',
            join: { table: 'transactions', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'customer_id' }],
            metrics: [
                { fn: 'AVG', column: 'income' },
                { fn: 'SUM', column: 'amount' },
            ],
            filters: [{ column: 'signup_date', operator: '>=', value: '2025-04-01' }, { column: 'signup_date', operator: '<', value: '2025-07-01' }]
        },
        hint: `**Step-by-step**:
        1. Start with the **customers** table and join **transactions** on \`customer_id\`.
        2. Filter to customers who signed up in **Q2 2025** (April 1 - June 30) to keep the chart readable.
        3. Group by **customer_id** (one dot per customer).
        4. Add metrics for **AVG(income)** and **SUM(amount)**.
        5. Select the **Scatter** chart type.`
    },
    {
        id: 'credit-score-vs-spending',
        level: 3,
        title: 'Q7: Do customers with higher credit scores spend more?',
        businessContext: `Credit score is a measure of financial responsibility - but does it predict *spending behavior* in our app? A customer with a high credit score might be conservative with spending, or they might be confident high-spenders. **Let's find out empirically rather than assuming.**`,
        teach: `Reuse the join between \`customers\` and \`transactions\`. Group by \`customer_id\` again, but this time plot **AVG(credit_score)** on one axis and **SUM(amount)** on the other. Filter to recent sign-ups to keep the chart readable. A positive correlation would suggest credit score is a useful proxy for customer value.`,
        explanation: `This tests a common FinTech assumption. If there's no clear correlation between credit score and spending, then credit score may not be a reliable proxy for customer lifetime value - which has direct implications for targeting models and underwriting strategy.`,
        expectedChartType: 'scatter',
        goal: 'Plot credit score vs total transaction amount per customer.',
        answer: {
            table: 'customers',
            join: { table: 'transactions', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'customer_id' }],
            metrics: [
                { fn: 'AVG', column: 'credit_score' },
                { fn: 'SUM', column: 'amount' },
            ],
            filters: [{ column: 'signup_date', operator: '>=', value: '2025-04-01' }, { column: 'signup_date', operator: '<', value: '2025-07-01' }]
        },
        hint: `**Step-by-step**:
        1. Join **customers** and **transactions** on \`customer_id\`.
        2. Filter to customers who signed up in **Q2 2025** (April 1 - June 30) to keep the chart readable.
        3. Group by **customer_id**.
        4. Add metrics for **AVG(credit_score)** and **SUM(amount)**.
        5. Select the **Scatter** chart type and look for a trend.`
    },
    {
        id: 'high-income-spending-drivers',
        level: 3,
        title: 'Q8: Drivers of high income spending - volume or ticket size?',
        businessContext: `We've seen that higher income customers tend to spend more. But *how* do they spend more? **Do they transact more frequently (high volume), or do they make bigger individual purchases (high average amount)?** These are very different business patterns with different implications for marketing and product.`,
        teach: `Join \`customers\` and \`transactions\`. Group by \`income\` with a bucket size (e.g. 20000) to segment the population. Add two metrics: **COUNT(transaction_id)** for volume and **AVG(amount)** for ticket size. Compare these side-by-side in a **Bar** chart.`,
        explanation: `Knowing whether high-income customers spend more per transaction or simply transact more often changes the strategic response. High average ticket size suggests premium positioning works - reinforce it. High frequency suggests a habitual use pattern that loyalty programmes and push notifications can amplify.`,
        expectedChartType: 'bar',
        goal: 'Compare average transaction amount and transaction count across income buckets.',
        answer: {
            table: 'customers',
            join: { table: 'transactions', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'income', bucket: { size: 20000 } }],
            metrics: [
                { fn: 'COUNT', column: 'transaction_id' },
                { fn: 'AVG', column: 'amount' }
            ]
        },
        hint: `**Step-by-step**:
        1. Join **customers** and **transactions** on \`customer_id\`.
        2. Group by **income** with a bucket size of \`20000\`.
        3. Add two metrics: **COUNT(transaction_id)** and **AVG(amount)**.
        4. View as a **Bar** chart to compare across income bands.`
    },
    {
        id: 'avg-value-by-country',
        level: 4,
        title: 'Q9: Which countries generate the highest average value per customer?',
        businessContext: `Total revenue by country is useful, but it's biased toward countries with more customers. **A country with 10 customers spending $10,000 each is more interesting than a country with 1,000 customers spending $100 each.** Average revenue per customer strips out population size and reveals where our most valuable users actually are.`,
        teach: `Join \`transactions\` and \`customers\` on \`customer_id\`. Group by \`country\`. Sum the \`amount\` to get total revenue per country, and divide by the count of distinct \`customer_id\` values to get average value per customer. View as a **Bar** chart sorted by this derived metric.`,
        explanation: `Average revenue per customer strips out population size effects and surfaces where our most valuable users actually are. A country that ranked low on total revenue in Q2 may climb dramatically here, revealing high-quality markets that deserve more focused investment.`,
        expectedChartType: 'bar',
        goal: 'Calculate and visualize SUM(amount) / COUNT(DISTINCT customer_id) by country.',
        answer: {
            table: 'transactions',
            join: { table: 'customers', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'country' }],
            aggregation: { fn: 'SUM', column: 'amount' }
        },
        hint: `**Step-by-step**:
        1. Join **transactions** and **customers** on \`customer_id\`.
        2. Group by **country**.
        3. Add metric **SUM(amount)** for total revenue per country.
        4. View as a **Bar** chart and compare vs Q2 to see if the rankings change.`
    },
    {
        id: 'highest-value-customers-map',
        level: 4,
        title: 'Q10: Where are the highest value customers located globally?',
        businessContext: `We've analysed customer counts, total revenue, and income distributions. Now let's bring it all together on a single map: **where in the world do our most valuable customers live?** This final view combines everything - geography, revenue, and customer quality - into one executive-level picture.`,
        teach: `This is the same structure as Q2, but now you can interpret the result with full context from the previous questions. Join \`transactions\` and \`customers\` on \`customer_id\`, group by \`country\`, and use **SUM(amount)** as the metric. The **Map** chart will show a global heat-map of customer value.`,
        explanation: `This final map combines everything learned across the module - geography, revenue, and customer quality - into one executive-level picture. The darkest countries are your highest-priority growth markets. Cross-referencing with Q1 reveals whether that revenue comes from scale (more customers) or quality (higher spend per customer).`,
        expectedChartType: 'map',
        goal: 'Map countries colored by their total or average transaction value.',
        answer: {
            table: 'transactions',
            join: { table: 'customers', type: 'INNER', on: 'customer_id' },
            groupBy: [{ column: 'country' }],
            aggregation: { fn: 'SUM', column: 'amount' }
        },
        hint: `**Step-by-step**:
        1. Join **transactions** and **customers** on \`customer_id\`.
        2. Group by **country**.
        3. Set metric to **SUM(amount)**.
        4. Select the **Map** visualization.
        5. Compare the result with Q2 - do the same countries dominate?`
    }
];

export const validateAnswer = (userQuery, challenge) => {
    // Shared generic validation function with dataVizChallenges.js behavior
    const { answer, expectedChartType } = challenge;

    // 1. Check Table
    if (userQuery.table !== answer.table) {
        return { passed: false, feedback: `Incorrect primary table. You selected ${userQuery.table}, but should use ${answer.table}.` };
    }

    // 2. Check Joins (if required)
    if (answer.join) {
        if (!userQuery.join || userQuery.join.table !== answer.join.table) {
            return { passed: false, feedback: `You need to JOIN the ${answer.join.table} table.` };
        }
    }

    // 3. Check Chart Type
    if (userQuery.chartType !== expectedChartType && userQuery.chartType !== 'metric') {
        const typeNames = {
            'bar': 'Bar Chart',
            'histogram': 'Histogram',
            'line': 'Line Chart',
            'area': 'Area Chart',
            'combo': 'Combo Chart',
            'dual-axis': 'Dual Axis Chart',
            'pie': 'Pie Chart',
            'metric': 'Metric Card',
            'map': 'Map',
            'scatter': 'Scatter Plot'
        };
        return { passed: false, feedback: `Incorrect chart type. Expected a ${typeNames[expectedChartType] || expectedChartType}.` };
    }

    // We do loose validation for the rest so users have freedom to explore
    return { passed: true, feedback: 'Great job! You visualized the data correctly.' };
};
